import { useEffect, useState } from 'react'
import { campaignsApi, type Campaign } from '../api/campaigns'
import { fetchPipeline, fetchPipelines, type Pipeline, type PipelineStage } from '../api/pipelines'
import {
  LEAD_BULK_OPERATION_LABELS,
  leadBulkApi,
  type LeadBulkOperation,
  type LeadBulkParams,
  type LeadBulkPreview,
  type LeadBulkResult,
} from '../api/leadBulk'
import { JobProgressToast } from './JobProgressToast'
import { Button, Callout, Card, Dialog, DialogSteps, FormGrid, List, ListItem, LoadingState, Select, StatusPill, TextField } from './ui'

/**
 * F-066 (TASK-0320, REQ-241-001): operazioni multiple sull'elenco lead. Flusso: scelta dell'operazione sui lead
 * selezionati, anteprima (nessuna scrittura: per ogni lead dice se verrebbe cambiato, e' gia' a posto, ha una
 * transizione non ammessa o non e' raggiungibile), conferma. Fino alla soglia del server l'esito arriva subito;
 * oltre, il lavoro prosegue in background e un avviso mostra l'avanzamento senza bloccare la pagina.
 * La verifica dei permessi la fa il server per ogni lead: quelli non visibili compaiono come "non trovati".
 */
export interface LeadBulkActionsBarProps {
  selectedIds: number[]
  onClear: () => void
  /** Chiamata quando qualcosa e' stato scritto (esecuzione immediata o job concluso): la pagina si ricarica. */
  onApplied: () => void
}

const LOST_REASONS = [
  'Prezzo', 'Non interessato', 'Già cliente', 'Vincolato con la concorrenza', 'Irraggiungibile',
  'Numero errato', 'Fuori target', 'Ripensamento', 'Documentazione mancante', 'Problema tecnico',
  'Copertura insufficiente', 'Offerta della concorrenza', 'Richiamare più avanti', 'Altro',
]

const CONTEST_REASONS = [
  'Numero falso', 'Duplicato', 'Fuori zona', 'Cliente già acquisito', 'Informazioni errate', 'Fuori target',
]

const STATUS_OPTIONS = [
  { key: 'contacted', label: 'Contattato' },
  { key: 'qualified', label: 'Qualificato' },
  { key: 'lost', label: 'Perso' },
]

const PRIORITY_OPTIONS = [
  { key: 'low', label: 'Bassa' },
  { key: 'medium', label: 'Media' },
  { key: 'high', label: 'Alta' },
]

const OUTCOME_LABELS: Record<string, string> = {
  Applicabile: 'Verrà modificato',
  GiaApplicato: 'Già a posto',
  TransizioneNonValida: 'Passaggio non ammesso',
  NonTrovato: 'Non trovato o non accessibile',
}

const OPERATIONS = Object.keys(LEAD_BULK_OPERATION_LABELS) as LeadBulkOperation[]

type Phase = 'form' | 'preview' | 'running' | 'done'

function emptyParams(): LeadBulkParams {
  return {}
}

/** Parametri pronti per il server: solo i campi che servono all'operazione scelta. */
function paramsFor(operation: LeadBulkOperation, p: LeadBulkParams): LeadBulkParams {
  switch (operation) {
    case 'cambia_stato':
      return {
        ...(p.status ? { status: p.status } : {}),
        ...(p.reason ? { reason: p.reason } : {}),
        ...(p.note ? { note: p.note } : {}),
      }
    case 'priorita':
      return p.priority ? { priority: p.priority } : {}
    case 'aggiungi_campagna':
    case 'rimuovi_campagna':
      return p.campaignId ? { campaignId: p.campaignId } : {}
    case 'pipeline':
      return {
        ...(p.pipelineId ? { pipelineId: p.pipelineId } : {}),
        ...(p.stageId ? { stageId: p.stageId } : {}),
      }
    case 'contesta':
      return p.reason ? { reason: p.reason } : {}
  }
}

/** Completezza minima lato UI, per non far partire un'anteprima ovviamente vuota; la validita' la decide il server. */
function paramsComplete(operation: LeadBulkOperation, p: LeadBulkParams): boolean {
  switch (operation) {
    case 'cambia_stato': return !!p.status && (p.status !== 'lost' || !!p.reason)
    case 'priorita': return !!p.priority
    case 'aggiungi_campagna':
    case 'rimuovi_campagna': return !!p.campaignId
    case 'pipeline': return !!p.pipelineId
    case 'contesta': return !!p.reason
  }
}

export function LeadBulkActionsBar({ selectedIds, onClear, onApplied }: LeadBulkActionsBarProps) {
  const [operation, setOperation] = useState<LeadBulkOperation | ''>('')
  const [open, setOpen] = useState(false)
  const [params, setParams] = useState<LeadBulkParams>(emptyParams)
  const [phase, setPhase] = useState<Phase>('form')
  const [preview, setPreview] = useState<LeadBulkPreview | null>(null)
  const [result, setResult] = useState<LeadBulkResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [job, setJob] = useState<{ id: number; title: string } | null>(null)

  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [stages, setStages] = useState<PipelineStage[]>([])

  // Elenchi per le operazioni che li usano: caricati solo quando servono.
  useEffect(() => {
    if (!open) return
    if ((operation === 'aggiungi_campagna' || operation === 'rimuovi_campagna') && campaigns.length === 0) {
      campaignsApi.list({ page: 1 }).then(p => setCampaigns(p.items)).catch(() => setCampaigns([]))
    }
    if (operation === 'pipeline' && pipelines.length === 0) {
      fetchPipelines().then(setPipelines).catch(() => setPipelines([]))
    }
  }, [open, operation, campaigns.length, pipelines.length])

  useEffect(() => {
    if (operation !== 'pipeline' || !params.pipelineId) {
      setStages([])
      return
    }
    let cancelled = false
    fetchPipeline(params.pipelineId)
      .then(p => { if (!cancelled) setStages(p.stages) })
      .catch(() => { if (!cancelled) setStages([]) })
    return () => { cancelled = true }
  }, [operation, params.pipelineId])

  function openDialog(): void {
    if (!operation) return
    setParams(emptyParams())
    setPreview(null)
    setResult(null)
    setError(null)
    setPhase('form')
    setOpen(true)
  }

  function closeDialog(): void {
    if (busy) return
    setOpen(false)
  }

  function patch(p: Partial<LeadBulkParams>): void {
    setParams(prev => ({ ...prev, ...p }))
    setPreview(null)
  }

  // Avanti dai parametri: se l'anteprima e' ancora valida (i parametri non sono cambiati dopo "Indietro") si torna
  // semplicemente al passo successivo, senza rifare la chiamata.
  function goToPreview(): void {
    if (preview) setPhase('preview')
    else void runPreview()
  }

  async function runPreview(): Promise<void> {
    if (!operation || busy) return
    setBusy(true)
    setError(null)
    try {
      const p = await leadBulkApi.preview({ operation, params: paramsFor(operation, params), leadIds: selectedIds })
      setPreview(p)
      setPhase('preview')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setBusy(false)
    }
  }

  async function confirm(): Promise<void> {
    if (!operation || busy) return
    setBusy(true)
    setError(null)
    setPhase('running')
    try {
      const outcome = await leadBulkApi.execute({
        operation, params: paramsFor(operation, params), leadIds: selectedIds,
      })
      if (outcome.kind === 'queued') {
        setJob({
          id: outcome.queued.jobId,
          title: `${LEAD_BULK_OPERATION_LABELS[operation]}: ${outcome.queued.queued} lead`,
        })
        setOpen(false)
        onClear()
      } else {
        setResult(outcome.result)
        setPhase('done')
        if (outcome.result.applied > 0) onApplied()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
      setPhase('preview')
    } finally {
      setBusy(false)
    }
  }

  const count = selectedIds.length
  const notApplicable = preview?.items.filter(i => i.outcome !== 'Applicabile') ?? []

  return (
    <>
      {count > 0 && (
        <Card variant="outlined" padding="sm">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <strong>{count} {count === 1 ? 'lead selezionato' : 'lead selezionati'}</strong>
            <Select
              label="Operazione"
              labelHidden
              aria-label="Operazione"
              value={operation}
              onChange={e => setOperation(e.target.value as LeadBulkOperation | '')}
            >
              <option value="">Scegli un'operazione…</option>
              {OPERATIONS.map(op => (
                <option key={op} value={op}>{LEAD_BULK_OPERATION_LABELS[op]}</option>
              ))}
            </Select>
            <Button type="button" variant="filled" size="sm" disabled={!operation} onClick={openDialog}>
              Continua
            </Button>
            <Button type="button" variant="text" size="sm" className="ms-auto" onClick={onClear}>
              Deseleziona tutto
            </Button>
          </div>
        </Card>
      )}

      {open && operation && (
        <Dialog
          open
          onClose={closeDialog}
          title={`${LEAD_BULK_OPERATION_LABELS[operation]} — ${count} lead`}
          as="form"
          onSubmit={(e) => {
            e.preventDefault()
            if (phase === 'form') { if (!busy && paramsComplete(operation, params)) goToPreview() }
            else if (phase === 'preview' && preview) { if (!busy && preview.applicabile !== 0) void confirm() }
          }}
          actions={
            phase === 'done' ? (
              <Button type="button" variant="text" dismiss onClick={() => { setOpen(false); onClear() }}>
                Chiudi
              </Button>
            ) : (
              <>
                {phase === 'preview' ? (
                  <Button type="button" variant="text" onClick={() => setPhase('form')} disabled={busy}>
                    Indietro
                  </Button>
                ) : (
                  <Button type="button" variant="text" onClick={closeDialog} disabled={busy}>
                    Annulla
                  </Button>
                )}
                {phase === 'form' && (
                  <Button
                    type="submit"
                    variant="text"
                    disabled={busy || !paramsComplete(operation, params)}
                  >
                    {busy ? 'Calcolo…' : 'Anteprima'}
                  </Button>
                )}
                {phase === 'preview' && preview && (
                  <Button
                    type="submit"
                    variant="text"
                    disabled={busy || preview.applicabile === 0}
                  >
                    {preview.applicabile === 0
                      ? 'Niente da modificare'
                      : `Conferma e modifica ${preview.applicabile} lead`}
                  </Button>
                )}
              </>
            )
          }
        >
          <DialogSteps
            current={phase === 'form' ? 1 : phase === 'done' ? 3 : 2}
            total={3}
            label={phase === 'form' ? 'Parametri' : phase === 'preview' ? 'Anteprima' : phase === 'running' ? 'Applicazione' : 'Esito'}
          />

          {phase === 'form' || phase === 'preview' ? (
            <ParamsForm
              operation={operation}
              params={params}
              campaigns={campaigns}
              pipelines={pipelines}
              stages={stages}
              onChange={patch}
            />
          ) : null}

          {error && <Callout tone="error">{error}</Callout>}

          {phase === 'preview' && preview && (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-wrap gap-2">
                <StatusPill tone="success">{preview.applicabile} da modificare</StatusPill>
                <StatusPill tone="neutral">{preview.giaApplicato} già a posto</StatusPill>
                <StatusPill tone="warning">{preview.transizioneNonValida} passaggi non ammessi</StatusPill>
                <StatusPill tone="neutral">{preview.nonTrovato} non trovati</StatusPill>
              </div>
              {preview.willRunInBackground && (
                <Callout tone="info">
                  Sono più di {preview.syncThreshold} lead: l'operazione proseguirà in background e potrai
                  continuare a lavorare, vedrai l'avanzamento in basso a destra.
                </Callout>
              )}
              {notApplicable.length > 0 && (
                <>
                  <h3 className="md-typescale-title-small">Lead che non verranno modificati</h3>
                  <div className="md-dialog__bleed">
                    <List aria-label="Lead che non verranno modificati">
                      {notApplicable.slice(0, 50).map(i => (
                        <ListItem
                          key={i.leadId}
                          headline={`Lead #${i.leadId} — ${OUTCOME_LABELS[i.outcome] ?? i.outcome}${i.detail ? ` (${i.detail})` : ''}`}
                        />
                      ))}
                      {notApplicable.length > 50 && <ListItem headline={`…e altri ${notApplicable.length - 50}`} />}
                    </List>
                  </div>
                </>
              )}
            </div>
          )}

          {phase === 'running' && <LoadingState />}

          {phase === 'done' && result && (
            <Callout tone="success">
              Operazione completata: {result.applied} lead modificati
              {result.alreadyApplied > 0 ? `, ${result.alreadyApplied} già a posto` : ''}
              {result.invalidTransition > 0 ? `, ${result.invalidTransition} passaggi non ammessi` : ''}
              {result.notFound > 0 ? `, ${result.notFound} non trovati` : ''}.
            </Callout>
          )}
        </Dialog>
      )}

      {job && (
        <JobProgressToast
          jobId={job.id}
          title={job.title}
          onClose={() => setJob(null)}
          onFinished={s => { if ((s.result?.['applied'] as number | undefined ?? 0) > 0) onApplied() }}
        />
      )}
    </>
  )
}

function ParamsForm({
  operation, params, campaigns, pipelines, stages, onChange,
}: {
  operation: LeadBulkOperation
  params: LeadBulkParams
  campaigns: Campaign[]
  pipelines: Pipeline[]
  stages: PipelineStage[]
  onChange: (p: Partial<LeadBulkParams>) => void
}) {
  switch (operation) {
    case 'cambia_stato':
      return (
        <FormGrid>
          <Select label="Nuovo stato"
            value={params.status ?? ''}
            onChange={e => onChange({ status: e.target.value })}
          >
            <option value="">Scegli…</option>
            {STATUS_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </Select>
          {params.status === 'lost' && (
            <Select label="Motivo"
              value={params.reason ?? ''}
              onChange={e => onChange({ reason: e.target.value })}
            >
              <option value="">Scegli…</option>
              {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </Select>
          )}
          <TextField
            label="Nota (facoltativa)"
            type="text"
            maxLength={500}
            value={params.note ?? ''}
            onChange={e => onChange({ note: e.target.value })}
          />
        </FormGrid>
      )
    case 'priorita':
      return (
        <FormGrid columns={1}>
          <Select label="Nuova priorità"
            value={params.priority ?? ''}
            onChange={e => onChange({ priority: e.target.value })}
          >
            <option value="">Scegli…</option>
            {PRIORITY_OPTIONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </Select>
        </FormGrid>
      )
    case 'aggiungi_campagna':
    case 'rimuovi_campagna':
      return (
        <FormGrid columns={1}>
          <Select label="Campagna"
            value={params.campaignId ?? ''}
            onChange={e => onChange({ campaignId: e.target.value ? Number(e.target.value) : undefined })}
          >
            <option value="">Scegli…</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </FormGrid>
      )
    case 'pipeline':
      return (
        <FormGrid>
          <Select label="Pipeline"
            value={params.pipelineId ?? ''}
            onChange={e => onChange({
              pipelineId: e.target.value ? Number(e.target.value) : undefined,
              stageId: undefined,
            })}
          >
            <option value="">Scegli…</option>
            {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Fase (facoltativa)"
            value={params.stageId ?? ''}
            disabled={!params.pipelineId}
            onChange={e => onChange({ stageId: e.target.value ? Number(e.target.value) : undefined })}
          >
            <option value="">Nessuna</option>
            {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </FormGrid>
      )
    case 'contesta':
      return (
        <FormGrid columns={1}>
          <Select label="Motivo della contestazione"
            value={params.reason ?? ''}
            onChange={e => onChange({ reason: e.target.value })}
          >
            <option value="">Scegli…</option>
            {CONTEST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </Select>
        </FormGrid>
      )
  }
}
